import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "../../../services/apiConfig";
import axiosInstance from "../../../services/axios.instance";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const VehicleCreation = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedMake, setSelectedMake] = useState<string>("");
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [customMake, setCustomMake] = useState<string>("");
  const [customModel, setCustomModel] = useState<string>("");

  interface MakeModelListResponse {
    [key: string]: string[];
  }

  const { data: makeModelData, isLoading } = useQuery<MakeModelListResponse>({
    queryKey: ["makeModelList"],
    queryFn: async () => {
      const response = await axiosInstance.get(API_URL.MAKE_MODEL_LIST);
      return response.data;
    },
  });

  const { mutate: createVehicle, isPending } = useMutation({
    mutationFn: async (payload: {
      vehicle_brand: string;
      vehicle_type: string;
    }) => {
      const response = await axiosInstance.post(
        API_URL.VEHICLE_CREATE,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Vehicle created successfully");
      queryClient.invalidateQueries({ queryKey: ["makeModelList"] });
      // Reset form
      setSelectedMake("");
      setSelectedModel("");
      setCustomMake("");
      setCustomModel("");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to create vehicle"
      );
    },
  });

  const handleMakeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMake(e.target.value);
    setSelectedModel("");
    setCustomMake("");
    setCustomModel("");
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedModel(e.target.value);
    setCustomModel("");
  };

  const handleCreateLocation = () => {
    navigate("/dashboard/locationCreation");
  };

  const handleSaveVehicle = () => {
    let brand = "";
    let type = "";

    // Determine values validation logic
    if (selectedMake === "Other Make") {
      brand = customMake;
      type = customModel;
    } else {
      brand = selectedMake;
      if (selectedModel === "Other Model") {
        type = customModel;
      } else {
        type = selectedModel;
      }
    }

    if (!brand?.trim() || !type?.trim()) {
      toast.error("Please fill in all fields (Make and Model)");
      return;
    }

    // Explicit payload mapping: 
    // Make -> vehicle_brand
    // Model -> vehicle_type
    const payload = {
      vehicle_brand: brand,
      vehicle_type: type,
    };

    console.log("Creating Vehicle with payload:", payload);
    createVehicle(payload);
  };

  const makes = makeModelData
    ? ["Other Make", ...Object.keys(makeModelData)]
    : ["Other Make"];

  // Logic for models:
  // If "Other Make" is selected, we don't show the model dropdown (based on previous logic implied by input replacement),
  // OR we show "Other Model" if that's the intent.
  // The previous code had: if selectedMake === "Others", text input for model.
  // The user says: "make and model is selected already existing then don't show create vehcile button".
  // This implies if I select "Other Make", I must input a custom make and custom model.
  // If I select a real Make, I can select a real Model OR "Other Model".
  
  const models =
    selectedMake && makeModelData && selectedMake !== "Other Make"
      ? ["Other Model", ...(makeModelData[selectedMake] || [])]
      : ["Other Model"];

  return (
    <div>
      <div className="col-12">
        <div className="row">
          <div className="col-md-12 text-start div-bg-invoice-details pt-3 position-relative">
            <div className="card card-bg-invoice-details">
              <div className="card-body px-5">
                <div className="position-absolute top-0 end-0 mt-3 me-3">
                  <button
                    className="btn all_button_style"
                    onClick={handleCreateLocation}
                  >
                    Create Location for Service Centre
                  </button>
                </div>
                <div className="pt-3">
                  <div className="row pt-3">
                    <div className="col-md-5 mb-3">
                      <label htmlFor="makeSelect" className="form-label">
                        Make
                      </label>
                      <select
                        id="makeSelect"
                        className="form-select"
                        value={selectedMake}
                        onChange={handleMakeChange}
                      >
                        <option value="">Select Make</option>
                        {makes.map((make) => (
                          <option key={make} value={make}>
                            {make}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-5 mb-3">
                      {selectedMake !== "Other Make" && (
                        <>
                          <label htmlFor="modelSelect" className="form-label">
                            Model
                          </label>
                          <select
                            id="modelSelect"
                            className="form-select"
                            value={selectedModel}
                            onChange={handleModelChange}
                            disabled={!selectedMake}
                          >
                            <option value="">Select Model</option>
                            {models.map((model: string) => (
                              <option key={model} value={model}>
                                {model}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Custom Inputs Row */}
                  {(selectedMake === "Other Make" ||
                    selectedModel === "Other Model") && (
                    <div className="row">
                      {selectedMake === "Other Make" && (
                        <div className="col-md-5 mb-3">
                          <label className="form-label">Enter Make</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Make"
                            value={customMake}
                            onChange={(e) => setCustomMake(e.target.value)}
                          />
                        </div>
                      )}
                      
                      {(selectedMake === "Other Make" || selectedModel === "Other Model") && (
                        <div className="col-md-5 mb-3">
                          <label className="form-label">Enter Model</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Model"
                            value={customModel}
                            onChange={(e) => setCustomModel(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {(selectedMake === "Other Make" ||
                    selectedModel === "Other Model") && (
                    <div className="row">
                      <div className="col-12 text-end">
                        <button
                          className="btn btn-success"
                          onClick={handleSaveVehicle}
                          disabled={isPending}
                        >
                          {isPending ? "Creating..." : "Create Vehicle"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleCreation;
